import Cookies from 'js-cookie';
import { getBaseUrl } from '../utils/apiHelper';

const API_URL = `${getBaseUrl()}/api/emails`;

const emailService = {
    receiveEmail: async (email, files) => {
        try {
            console.log("📧 Sending email data to server...");
            const formData = new FormData();
            formData.append("email", email);
            files.forEach(file => formData.append("files", file));

            const token = Cookies.get("token");
            const response = await fetch(`${API_URL}/receive`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || "Failed to process email.");
            }

            console.log("✅ Email processed successfully:", data);
            return data;
        } catch (error) {
            console.error("❌ Error processing email:", error.message);
            throw error;
        }
    },

    createEmail: async (emailData) => {
        try {
            console.log("📧 Creating new email...");
            const token = Cookies.get("token");
            const response = await fetch(`${API_URL}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(emailData),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || "Failed to create email.");
            }

            console.log("✅ Email created successfully:", data.email);
            return data.email;
        } catch (error) {
            console.error("❌ Error creating email:", error.message);
            throw error;
        }
    },

    getAllEmails: async () => {
        try {
            console.log("📧 Fetching all emails...");
            const token = Cookies.get("token");
            const response = await fetch(`${API_URL}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || "Failed to fetch emails.");
            }

            console.log("✅ Emails fetched successfully:", data.emails);
            return data.emails.map(email => ({
                ...email,
                comment: email.comment || "", // Ensure the comment field is included
            }));
        } catch (error) {
            console.error("❌ Error fetching emails:", error.message);
            throw error;
        }
    },

    getEmailsByClient: async (clientId) => {
        try {
            console.log(`📧 Fetching emails for client ID: ${clientId}...`);
            const token = Cookies.get("token");
            const response = await fetch(`${API_URL}/client/${clientId}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || "Failed to fetch emails for client.");
            }

            // Make sure comments are included in the response logging
            if (data.emails && data.emails.length > 0) {
                console.log(`📧 Comments from first email: ${data.emails[0].comment || 'No comment'}`);
            }
            
            console.log("✅ Emails for client fetched successfully:", data.emails);
            
            // Ensure comment field is properly preserved in each email object
            return data.emails.map(email => ({
                ...email,
                comment: email.comment || "", // Ensure comment field exists and has at least an empty string
            }));
        } catch (error) {
            console.error("❌ Error fetching emails for client:", error.message);
            throw error;
        }
    },

    updateEmailReviewStatus: async (id, isReviewed) => {
        try {
            console.log(`📧 Updating review status for email ID: ${id}...`);
            const token = Cookies.get("token");
            const response = await fetch(`${API_URL}/${id}/review-status`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ isReviewed }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || "Failed to update email review status.");
            }

            console.log("✅ Email review status updated successfully:", data.email);
            return data.email;
        } catch (error) {
            console.error("❌ Error updating email review status:", error.message);
            throw error;
        }
    },    toggleReviewStatus: async (id) => {
        try {
            console.log(`📧 Toggling review status for email ID: ${id}...`);            
            const token = Cookies.get("token");
            
            if (!token) {
                console.error("❌ No authentication token found in cookies");
                // If we don't have a token, the user likely needs to log in again
                // Import and use the logout function from authUtils
                const { logout } = require('../utils/authUtils');
                logout();
                throw new Error("Authentication token not found. Please login again.");
            }
            
            // Only include headers that are needed and configured in the backend
            const headers = {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            };
            
            const response = await fetch(`${API_URL}/${id}/toggle-review`, {
                method: "PATCH",
                headers,
            });
            
            console.log('📥 Response status:', response.status, response.statusText);
            
            // Try to parse the response body as JSON
            let data;
            try {
                data = await response.json();
                console.log('📥 Response data:', data);
            } catch (parseError) {
                console.error('❌ Error parsing response:', parseError);
                throw new Error(`Failed to parse server response: ${parseError.message}`);
            }
            
            if (!response.ok) {
                console.error('❌ API error response:', data);
                throw new Error(data.message || "Failed to toggle email review status.");
            }

            console.log("✅ Email review status toggled successfully:", data.email);
            return data.email;
        } catch (error) {
            console.error("❌ Error toggling email review status:", error.message);
            throw error;
        }
    },

    updateEmail: async (id, emailData) => {
        try {
            console.log(`📧 Updating email ID: ${id}...`);
            const token = Cookies.get("token");
            const response = await fetch(`${API_URL}/${id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(emailData),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || "Failed to update email.");
            }

            console.log("✅ Email updated successfully:", data.email);
            return data.email;
        } catch (error) {
            console.error("❌ Error updating email:", error.message);
            throw error;
        }
    },

    deleteEmail: async (id) => {
        try {
            console.log(`📧 Deleting email ID: ${id}...`);
            const token = Cookies.get("token");
            const response = await fetch(`${API_URL}/${id}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || "Failed to delete email.");
            }

            console.log("✅ Email deleted successfully.");
            return data;
        } catch (error) {
            console.error("❌ Error deleting email:", error.message);
            throw error;
        }
    },

    addComment: async (emailId, commentText) => {
        try {
            console.log(`📧 Adding comment to email ID: ${emailId}...`);
            const token = Cookies.get("token");
            const response = await fetch(`${API_URL}/${emailId}/comment`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ comment: commentText }),
            });

            // Enhanced response handling
            if (!response.ok) {
                const contentType = response.headers.get("content-type");
                if (contentType && contentType.includes("application/json")) {
                    const data = await response.json();
                    throw new Error(data.message || "Failed to add comment to email.");
                } else {
                    const text = await response.text();
                    console.error("Non-JSON error response:", text);
                    throw new Error(`Server error (${response.status}): The comment endpoint may not be implemented correctly.`);
                }
            }

            const data = await response.json();
            console.log("✅ Comment added to email successfully:", data.email);
            
            // Log the specific comment field to verify it exists
            console.log("📧 Comment field on returned email:", data.email.comment);
            
            return data.email;
        } catch (error) {
            console.error("❌ Error adding comment to email:", error.message);
            throw error;
        }
    },

    updateComment: async (emailId, commentText) => {
        try {
            console.log(`📧 Updating comment for email ID: ${emailId}...`);
            const token = Cookies.get("token");
            const response = await fetch(`${API_URL}/${emailId}/comment`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ comment: commentText }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || "Failed to update comment for email.");
            }

            console.log("✅ Comment updated for email successfully:", data.email);
            return data.email;
        } catch (error) {
            console.error("❌ Error updating comment for email:", error.message);
            throw error;
        }
    },    getPaginatedEmails: async (page = 1, limit = 20, clientId = null) => {
        try {
            console.log(`📧 [PAGINATION DEBUG] Fetching paginated emails (page ${page}, limit ${limit}${clientId ? `, clientId: ${clientId}` : ''})...`);
            const token = Cookies.get("token");
            
            // Change the URL to avoid conflict with the :id pattern
            // Use '/api/emails' with query parameters instead
            let endpoint = `${API_URL}?pagination=true&page=${page}&limit=${limit}`;
            if (clientId) {
                endpoint += `&clientId=${clientId}`;
            }
            
            console.log(`📧 [PAGINATION DEBUG] Endpoint: ${endpoint}`);
            
            const response = await fetch(endpoint, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            console.log(`📧 [PAGINATION DEBUG] Response status: ${response.status} ${response.statusText}`);
            
            const data = await response.json();
            if (!response.ok) {
                console.error(`❌ [PAGINATION DEBUG] Failed to fetch paginated emails: ${JSON.stringify(data)}`);
                throw new Error(data.message || "Failed to fetch paginated emails.");
            }

            console.log(`✅ [PAGINATION DEBUG] Success! Received page ${data.page} of ${data.totalPages} (${data.emails?.length || 0} emails)`);
            console.log(`📧 [PAGINATION DEBUG] Total items: ${data.total || 'unknown'}`);
            
            // Validate that emails are for the requested client
            if (clientId) {
                const wrongClientEmails = data.emails.filter(email => email.clientId !== clientId);
                if (wrongClientEmails.length > 0) {
                    console.warn(`⚠️ [PAGINATION DEBUG] Found ${wrongClientEmails.length} emails with wrong clientId!`);
                    console.warn(`⚠️ Expected: ${clientId}, Found: ${wrongClientEmails.map(e => e.clientId).join(', ')}`);
                }
            }
            
            // Ensure comment field is properly preserved in each email
            return {
                ...data,
                emails: data.emails.map(email => ({
                    ...email,
                    comment: email.comment || "", 
                }))
            };
        } catch (error) {
            console.error(`❌ [PAGINATION DEBUG] Error fetching paginated emails: ${error.message}`);
            console.error(error);
            throw error;
        }
    },    getEmailById: async (emailId) => {
        try {
            console.log(`📧 Fetching email with ID: ${emailId}...`);
            const token = Cookies.get("token");
            const endpoint = `${API_URL}/${emailId}`;
            console.log(`📧 Using API endpoint: ${endpoint}`);
            const res = await fetch(endpoint, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.message || "Failed to fetch email");
            }            console.log("✅ Email fetched successfully:", data.email);
            return data.email;
        } catch (error) {
            console.error("❌ Error fetching email:", error.message);
            console.error("Full error object:", error);
            throw error;
        }
    },
};

export default emailService;
